import { COMPASS, HUD } from "odyssey-shared";
import { type HudArea, type HudSlot, type HudSlotId, type SlotRect } from "./slots.ts";
import { TOUCH_BUTTONS, TOUCH_EXTENT, type TouchButtonSpec } from "../touch/actions.ts";

/**
 * The HUD on a touch page held landscape (ADR-061's amendment of 2026-10-31, decision 9, P1.9-T51h; the mobile plan's
 * mockup): the surfaces that are drawn stand where the mockup puts them, the controls the page holds for itself keep
 * their ground, and every panel starts closed behind a button of its own. The player card, the target frame and the
 * ability bar keep their look and their slots; what moves is the menu and the Bag button (top right), the minimap (under
 * them), the chat bar (above the stick's resting base), the toast column (left of the minimap) and the message line
 * (between the stick and the buttons). The stance button and the tool belt are the cluster's own buttons here, and the
 * reservations nothing draws yet are hidden.
 */

/**
 * The room the action buttons take from the lower right corner: their width and height, a margin past the farthest
 * button (`TOUCH_EXTENT`).
 */
export const CLUSTER = TOUCH_EXTENT;

/**
 * The stick's resting base: its distance from the left and bottom edges and its width, the design's.
 */
export const STICK_BASE = { left: 12, bottom: 8, size: 64 } as const;

/**
 * The slots a touch page does not draw: the stance button and the tool belt (the cluster's Stance and Tool buttons), and
 * the reservations no surface fills yet.
 */
export const TOUCH_HIDDEN: readonly HudSlotId[] = ["stance", "tools", "party", "raid", "duel", "guildWar", "boss", "dungeon", "banner", "clock", "location", "quest", "maintenance"];

/**
 * Where the cluster's Tool button stands: its square from the area's top left (the cluster's corner at the right and
 * bottom safe insets, the button's `right` and `bottom` from the registry), which the tool belt's fan opens beside.
 *
 * @param area The area, with `touch` set.
 * @returns The rectangle.
 */
export function toolButtonRect(area: HudArea): SlotRect {
  const safe = area.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const button = TOUCH_BUTTONS.find((one) => one.id === "tool") as TouchButtonSpec;
  return { left: area.width - safe.right - button.right - button.size, top: area.height - safe.bottom - button.bottom - button.size, width: button.size, height: button.size };
}

/**
 * The room the touch controls take, as rectangles from the area's top left: the action buttons' corner and the stick's
 * resting base, which nothing the HUD draws may cover.
 *
 * @param area The area.
 * @returns The two rectangles.
 */
export function touchReserved(area: HudArea): readonly SlotRect[] {
  const safe = area.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  return [
    { left: area.width - safe.right - CLUSTER.width, top: area.height - safe.bottom - CLUSTER.height, width: CLUSTER.width, height: CLUSTER.height },
    { left: safe.left + STICK_BASE.left - HUD.margin, top: area.height - safe.bottom - STICK_BASE.bottom - STICK_BASE.size - HUD.margin, width: STICK_BASE.size + 2 * HUD.margin, height: STICK_BASE.size + STICK_BASE.bottom + 2 * HUD.margin },
  ];
}

/**
 * The side in logical pixels of the touch page's top row buttons, the Bag and the menu: a bare drawn icon on a square of
 * the touch icon set's size, which the page's scale carries past the 44 pixel tap minimum.
 */
export const TOP_BUTTON_SIDE = 32;

/**
 * The menu button's slot on a touch page: the button alone (the Esc key glyph is a keyboard's), on the top right.
 */
const MENU_BUTTON = { width: TOP_BUTTON_SIDE, height: TOP_BUTTON_SIDE } as const;

/**
 * Where the Bag button stands: a gutter left of the menu button.
 *
 * @param area The area.
 * @returns The rectangle from the area's top left.
 */
export function bagButtonRect(area: HudArea): SlotRect {
  const safe = area.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  return { left: area.width - safe.right - HUD.margin - MENU_BUTTON.width - HUD.gutter - TOP_BUTTON_SIDE, top: safe.top + HUD.margin, width: TOP_BUTTON_SIDE, height: TOP_BUTTON_SIDE };
}

/**
 * The slot a touch page draws a surface in, where it differs from the table's.
 *
 * @param id The slot.
 * @param area The area, with `touch` set.
 * @param base The table's slot.
 * @returns The slot, or null where the page draws none.
 */
export function touchSlot(id: HudSlotId, area: HudArea, base: HudSlot): HudSlot | null {
  if (TOUCH_HIDDEN.includes(id)) {
    return null;
  }
  const safe = area.safe ?? { top: 0, right: 0, bottom: 0, left: 0 };
  switch (id) {
    case "menu":
      return { ...base, ...MENU_BUTTON, x: HUD.margin + safe.right, y: HUD.margin + safe.top, anchor: "top-right" };
    case "minimap":
      return { ...base, x: HUD.margin + safe.right, y: HUD.margin + safe.top + MENU_BUTTON.height + HUD.gutter };
    case "toasts":
      return { ...base, anchor: "top-right", x: HUD.margin + safe.right + COMPASS.slot + HUD.gutter, y: HUD.margin + safe.top, height: 56, width: 116 };
    case "message": {
      const side = CLUSTER.width + HUD.gutter + Math.max(safe.left, safe.right);
      return { ...base, y: base.y + safe.bottom, width: Math.max(0, Math.min(base.width, area.width - 2 * side)) };
    }
    case "chat":
      return { ...base, anchor: "bottom-left", x: HUD.margin + safe.left, y: STICK_BASE.bottom + STICK_BASE.size + HUD.margin + safe.bottom };
    case "vitals":
    case "status":
    case "target":
    case "targetStatus":
      return { ...base, x: base.x + safe.left, y: base.y + safe.top };
    case "abilities":
    case "xp":
    case "cast":
      return { ...base, y: base.y + safe.bottom };
    default:
      return base;
  }
}
